// src/pages/EditProfilePage.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { ALLOWED_COUNTRIES } from "../utils/countries";
import { formatNigerPhone, normalizeNigerPhone } from "../utils/nigerPhone";
import { 
  FiUser, 
  FiBriefcase, 
  FiImage, 
  FiUploadCloud, 
  FiChevronDown,
  FiX,
  FiArrowLeft
} from "react-icons/fi";

const EditProfilePage = () => {
  const { t } = useTranslation();
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({});
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!user) {
      setPageLoading(false);
      return;
    }
    setPageLoading(true);
    try {
      const isSupplier = user.user_type === "SUPPLIER";
      const profileUrl = isSupplier ? "/profile/supplier/" : "/profile/buyer/";

      const [categoriesRes, profileRes] = await Promise.all([
        api.get("/categories/"),
        api.get(profileUrl),
      ]);

      const loadedProfileData = profileRes.data;
      setCategories(categoriesRes.data.results || categoriesRes.data);

      setFormData({
        first_name: loadedProfileData.user.first_name || "",
        last_name: loadedProfileData.user.last_name || "",
        phone_number: formatNigerPhone(loadedProfileData.user.phone_number || ""),
        country: loadedProfileData.user.country || "",
        company_name: loadedProfileData.company_name || "",
        name: loadedProfileData.name || "",
        about: loadedProfileData.about || "",
      });

      if (isSupplier) {
        setSelectedCategories(
          loadedProfileData.industries.map((cat) => cat.id)
        );
      } else {
        setSelectedCategories(
          loadedProfileData.interested_categories.map((cat) => cat.id)
        );
      }
      setImagePreview(loadedProfileData.logo_url);
    } catch (err) {
      console.error("Failed to load profile data:", err);
      setError("Could not load your profile for editing.");
    } finally {
      setPageLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });
  const handlePhoneChange = (e) =>
    setFormData({ ...formData, phone_number: formatNigerPhone(e.target.value) });
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };
  const handleCategoryToggle = (id) => {
    setSelectedCategories((p) =>
      p.includes(id) ? p.filter((i) => i !== id) : [...p, id]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const isSupplier = user.user_type === "SUPPLIER";
    const profileUrl = isSupplier ? "/profile/supplier/" : "/profile/buyer/";
    const payload = new FormData();

    Object.keys(formData).forEach((key) => {
      if (key === "phone_number") {
        const phoneNumber = normalizeNigerPhone(formData[key]);
        if (phoneNumber) {
          payload.append(key, phoneNumber);
        }
        return;
      }
      if (formData[key] !== null && formData[key] !== undefined) {
        payload.append(key, formData[key]);
      }
    });

    if (image) {
      payload.append("logo", image);
    }

    const categoryKey = isSupplier ? "industry_ids" : "category_ids";
    selectedCategories.forEach((id) => payload.append(categoryKey, id));

    try {
      await api.patch(profileUrl, payload);
      const userResponse = await api.get("/me/");
      completeOnboarding(userResponse.data);
      navigate("/profile");
    } catch (err) {
      console.error("Profile update failed:", err);
      if (err.response && err.response.data) {
        const errorMessages = Object.values(err.response.data).flat();
        setError(errorMessages.join(" "));
      } else {
        setError("Failed to update profile. An unknown error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] gap-8 bg-theme-bg">
        <div className="w-20 h-20 border-4 border-chafadi-primary/20 border-t-chafadi-primary rounded-full animate-spin shadow-2xl shadow-orange-500/10" />
        <div className="text-center space-y-2">
           <p className="text-[10px] font-black text-theme-text uppercase tracking-[0.4em]">Establishing Connection...</p>
           <p className="text-[8px] font-black text-theme-muted uppercase tracking-widest animate-pulse">Please wait...</p>
        </div>
      </div>
    );

  const isSupplier = user.user_type === "SUPPLIER";

  return (
    <div className="bg-[#fcfcfc] min-h-screen py-10 font-sans text-[#1d1d1f]">
      <div className="max-w-3xl mx-auto px-4">
        
        {/* BACK BUTTON */}
        <Link to="/profile" className="inline-flex items-center gap-2 text-xs font-bold text-gray-400 hover:text-[#ff7a00] uppercase tracking-wider mb-8 transition-colors group">
          <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" />
          {t("common.back") || "Back"}
        </Link>

        <div className="bg-white border border-gray-100 rounded-3xl p-8 md:p-12 shadow-sm">
          <div className="mb-12">
            <h1 className="text-3xl font-bold mb-2">Edit Profile</h1>
            <p className="text-sm text-gray-400 font-medium">Update your account information and preferences.</p>
          </div>

          {error && (
            <div className="mb-10 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-xs font-bold uppercase tracking-wider">
              <FiX size={16} /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-12">
            
            {/* AVATAR / LOGO SECTION */}
            <div className="flex flex-col md:flex-row items-center gap-8 pb-12 border-b border-gray-50">
              <div className="relative group">
                <div className="w-32 h-32 rounded-2xl bg-gray-50 border border-gray-100 overflow-hidden group-hover:border-[#ff7a00]/30 transition-all shadow-inner">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-200">
                      <FiImage size={40} />
                    </div>
                  )}
                </div>
                <label htmlFor="logo-upload" className="absolute -bottom-2 -right-2 w-10 h-10 bg-[#ff7a00] text-white rounded-xl flex items-center justify-center shadow-lg cursor-pointer hover:bg-[#e66c00] transition-all">
                  <FiUploadCloud size={20} />
                  <input type="file" id="logo-upload" className="sr-only" onChange={handleImageChange} accept="image/*" />
                </label>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-sm font-bold mb-1">Profile Photo</h3>
                <p className="text-xs text-gray-400 font-medium leading-relaxed max-w-xs">Upload your company logo or personal avatar. Recommended size: 512x512px.</p>
              </div>
            </div>

            {/* PERSONAL INFORMATION */}
            <div className="space-y-8">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <FiUser className="text-[#ff7a00]" /> {t("profilePage.personalDetails") || "Personal Information"}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("authPage.firstName")}</label>
                  <input type="text" name="first_name" value={formData.first_name || ""} onChange={handleInputChange} className="w-full h-11 bg-gray-50 rounded-lg px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{t("authPage.lastName")}</label>
                  <input type="text" name="last_name" value={formData.last_name || ""} onChange={handleInputChange} className="w-full h-11 bg-gray-50 rounded-lg px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Phone Number</label>
                  <input
                    type="tel"
                    name="phone_number"
                    value={formData.phone_number || ""}
                    onChange={handlePhoneChange}
                    placeholder="+227 80 00 00 00"
                    className="w-full h-11 bg-gray-50 rounded-lg px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Country</label>
                  <div className="relative">
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full h-11 bg-gray-50 rounded-lg px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 appearance-none transition-all cursor-pointer"
                    >
                      {ALLOWED_COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                    <FiChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* ORGANIZATION DETAILS */}
            <div className="space-y-8 pt-8 border-t border-gray-50">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <FiBriefcase className="text-[#ff7a00]" /> {isSupplier ? "Company Details" : "Account Identity"}
              </h3>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">{isSupplier ? "COMPANY NAME" : "NAME"}</label>
                  <input type="text" name={isSupplier ? "company_name" : "name"} value={isSupplier ? formData.company_name || "" : formData.name || ""} onChange={handleInputChange} required className="w-full h-11 bg-gray-50 rounded-lg px-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all" />
                </div>

                {isSupplier && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">About</label>
                    <textarea rows={4} name="about" value={formData.about || ""} onChange={handleInputChange} placeholder="Describe your company..." className="w-full bg-gray-50 rounded-xl p-4 font-bold text-sm outline-none border border-transparent focus:border-[#ff7a00]/20 transition-all placeholder:text-gray-300 resize-none" />
                  </div>
                )}
              </div>
            </div>

            {/* CATEGORIES / INTERESTS */}
            <div className="space-y-8 pt-8 border-t border-gray-50">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                {isSupplier ? "Industries" : "Interests"}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategoryToggle(cat.id)}
                    className={`h-11 rounded-lg text-[10px] font-bold uppercase tracking-wider px-3 transition-all border ${
                      selectedCategories.includes(cat.id)
                        ? "bg-[#ff7a00] border-[#ff7a00] text-white shadow-md shadow-orange-500/10"
                        : "bg-white border-gray-100 text-gray-600 hover:border-[#ff7a00]/30 hover:text-[#ff7a00]"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* ACTION BUTTONS */}
            <div className="flex flex-col sm:flex-row gap-4 pt-12 border-t border-gray-100">
              <button 
                type="button"
                onClick={() => navigate("/profile")}
                className="h-14 flex-1 font-bold text-xs text-gray-400 uppercase tracking-widest rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading}
                className="h-14 flex-[2] bg-[#ff7a00] text-white font-bold rounded-xl shadow-lg shadow-orange-500/10 hover:bg-[#e66c00] transition-all flex items-center justify-center gap-3 disabled:opacity-50 uppercase text-xs tracking-widest"
              >
                {loading ? (
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};


export default EditProfilePage;
